import { useState, useEffect } from "react";
import { useNavigationType, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { v4 as uuidv4 } from "uuid";

import { RoomManagerData, RoomManagerRoom, RoomManagerZone } from "api/room-manager";

export interface SetupProps {
  data: RoomManagerData;
  onSave: (data: RoomManagerData) => void;
}

export default function Setup(props: SetupProps) {
  const { data, onSave } = props;

  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<RoomManagerZone | null>(null);
  const [modalZoneName, setModalZoneName] = useState("");
  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<RoomManagerRoom | null>(null);
  const [modalRoomName, setModalRoomName] = useState("");
  const [modalRoomZoneId, setModalRoomZoneId] = useState<string>("");
  const [modalRoomCapacity, setModalRoomCapacity] = useState<string>("");

  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  // ── Zone handlers ─────────────────────────────────────────────────────

  function openZoneModal(zone?: RoomManagerZone) {
    if (zone) {
      // Edit mode
      setEditingZone(zone);
      setModalZoneName(zone.name);
    } else {
      // Create mode
      setEditingZone(null);
      setModalZoneName("");
    }
    setIsZoneModalOpen(true);
    navigate(location.pathname);
  }

  function closeZoneModalUI() {
    setIsZoneModalOpen(false);
    setEditingZone(null);
    setModalZoneName("");
  }

  function closeZoneModal() {
    closeZoneModalUI();
    navigate(-1);
  }

  function handleSaveZone() {
    const trimmed = modalZoneName.trim();
    if (!trimmed) return;

    if (editingZone) {
      // Edit existing zone
      const updatedZones = data.zones.map((z) => (z.id === editingZone.id ? { ...z, name: trimmed } : z));
      onSave({ ...data, zones: updatedZones });
    } else {
      // Add new zone
      const newZone: RoomManagerZone = { id: uuidv4(), name: trimmed };
      onSave({ ...data, zones: [...data.zones, newZone] });
    }

    closeZoneModal();
  }

  function handleDeleteZone(zoneId: string) {
    const updatedZones = data.zones.filter((z) => z.id !== zoneId);
    // Unassign rooms that belonged to the deleted zone
    const updatedRooms = data.rooms.map((r) => (r.zoneId === zoneId ? { ...r, zoneId: null } : r));
    onSave({ zones: updatedZones, rooms: updatedRooms });
  }

  /**
   * On pressing back button close opened modals
   */
  useEffect(() => {
    if (navigationType === "POP") {
      closeZoneModalUI();
      closeRoomModalUI();
    }
  }, [navigationType]);

  // ── Room handlers ─────────────────────────────────────────────────────

  function openRoomModal(room?: RoomManagerRoom) {
    if (room) {
      // Edit mode
      setEditingRoom(room);
      setModalRoomName(room.name);
      setModalRoomZoneId(room.zoneId || "");
      setModalRoomCapacity(room.capacity !== null ? room.capacity.toString() : "");
    } else {
      // Create mode
      setEditingRoom(null);
      setModalRoomName("");
      setModalRoomZoneId("");
      setModalRoomCapacity("");
    }
    setIsRoomModalOpen(true);
    navigate(location.pathname);
  }

  function closeRoomModalUI() {
    setIsRoomModalOpen(false);
    setEditingRoom(null);
    setModalRoomName("");
    setModalRoomZoneId("");
    setModalRoomCapacity("");
  }

  function closeRoomModal() {
    closeRoomModalUI();
    navigate(-1);
  }

  function handleSaveRoom() {
    const trimmed = modalRoomName.trim();
    if (!trimmed) return;

    const capacityNum = modalRoomCapacity !== "" ? parseInt(modalRoomCapacity, 10) : null;

    if (editingRoom) {
      // Edit existing room
      const updatedRooms = data.rooms.map((r) =>
        r.id === editingRoom.id
          ? {
              ...r,
              name: trimmed,
              zoneId: modalRoomZoneId || null,
              capacity: capacityNum !== null && !Number.isNaN(capacityNum) && capacityNum > 0 ? capacityNum : null,
            }
          : r
      );
      onSave({ ...data, rooms: updatedRooms });
    } else {
      // Add new room
      const newRoom: RoomManagerRoom = {
        id: uuidv4(),
        name: trimmed,
        zoneId: modalRoomZoneId || null,
        capacity: capacityNum !== null && !Number.isNaN(capacityNum) && capacityNum > 0 ? capacityNum : null,
        userIds: [],
      };
      onSave({ ...data, rooms: [...data.rooms, newRoom] });
    }

    closeRoomModal();
  }

  function handleDeleteRoom(roomId: string) {
    onSave({ ...data, rooms: data.rooms.filter((r) => r.id !== roomId) });
  }

  return (
    <Box maxWidth='sm' margin='auto'>
      {/* ── Zones ── */}
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
        <Typography variant='subtitle1' fontWeight='bold'>
          Zones
        </Typography>
        <Button onClick={() => openZoneModal()} size='small' variant='contained'>
          Add
        </Button>
      </Box>

      {data.zones.length > 0 && (
        <List dense disablePadding sx={{ mb: 2 }}>
          {data.zones.map((zone) => (
            <ListItemButton key={zone.id} onClick={() => openZoneModal(zone)} sx={{ borderRadius: 1, mb: 0.5 }}>
              <ListItemText primary={zone.name} />
              <Tooltip title='Delete zone'>
                <IconButton
                  edge='end'
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteZone(zone.id);
                  }}
                >
                  <Icon path={mdiClose} size={0.8} />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          ))}
        </List>
      )}

      {data.zones.length === 0 && (
        <Typography variant='body2' color='text.secondary' mb={2}>
          No zones added yet.
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      {/* ── Rooms by Zone ── */}
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
        <Typography variant='subtitle1' fontWeight='bold'>
          Rooms
        </Typography>
        <Button onClick={() => openRoomModal()} size='small' variant='contained'>
          Add
        </Button>
      </Box>

      {data.rooms.length > 0 ? (
        <Box>
          {/* Rooms grouped by zone */}
          {data.zones.length > 0 && (
            <Box mb={2}>
              {data.zones.map((zone) => {
                const zoneRooms = data.rooms.filter((r) => r.zoneId === zone.id);
                if (zoneRooms.length === 0) return null;
                return (
                  <Box key={zone.id} mb={2}>
                    <Typography variant='overline' color='text.secondary' sx={{ px: 1, pb: 0.5, display: "block" }}>
                      {zone.name}
                    </Typography>
                    <List dense disablePadding>
                      {zoneRooms.map((room) => (
                        <ListItemButton
                          key={room.id}
                          onClick={() => openRoomModal(room)}
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={room.name}
                            secondary={
                              room.capacity !== null ? (
                                <Typography variant='caption' color='text.secondary'>
                                  {room.capacity} {room.capacity === 1 ? "person" : "people"}
                                </Typography>
                              ) : undefined
                            }
                          />
                          <Tooltip title='Delete room'>
                            <IconButton
                              edge='end'
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRoom(room.id);
                              }}
                            >
                              <Icon path={mdiClose} size={0.8} />
                            </IconButton>
                          </Tooltip>
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Ungrouped rooms */}
          {(() => {
            const ungroupedRooms = data.rooms.filter((r) => !r.zoneId);
            if (ungroupedRooms.length === 0) return null;
            return (
              <Box>
                <Typography variant='overline' color='text.secondary' sx={{ px: 1, pb: 0.5, display: "block" }}>
                  Ungrouped
                </Typography>
                <List dense disablePadding>
                  {ungroupedRooms.map((room) => (
                    <ListItemButton key={room.id} onClick={() => openRoomModal(room)} sx={{ borderRadius: 1, mb: 0.5 }}>
                      <ListItemText
                        primary={room.name}
                        secondary={
                          room.capacity !== null ? (
                            <Typography variant='caption' color='text.secondary'>
                              {room.capacity} {room.capacity === 1 ? "person" : "people"}
                            </Typography>
                          ) : undefined
                        }
                      />
                      <Tooltip title='Delete room'>
                        <IconButton
                          edge='end'
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRoom(room.id);
                          }}
                        >
                          <Icon path={mdiClose} size={0.8} />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            );
          })()}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary' mt={1}>
          No rooms added yet.
        </Typography>
      )}

      {/* ── Zone Form Modal ── */}
      <Dialog open={isZoneModalOpen} onClose={closeZoneModal} maxWidth='xs' fullWidth>
        <DialogTitle>{editingZone ? "Edit Zone" : "Add Zone"}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              autoFocus
              fullWidth
              label='Name'
              placeholder='e.g. Building A'
              size='small'
              value={modalZoneName}
              onChange={(e) => setModalZoneName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSaveZone();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeZoneModal}>Cancel</Button>
          <Button onClick={handleSaveZone} disabled={!modalZoneName.trim()} variant='contained'>
            {editingZone ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Room Form Modal ── */}
      <Dialog open={isRoomModalOpen} onClose={closeRoomModal} maxWidth='xs' fullWidth>
        <DialogTitle>{editingRoom ? "Edit Room" : "Add Room"}</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} pt={1}>
            <TextField
              autoFocus
              fullWidth
              label='Name'
              placeholder='e.g. Room 101'
              size='small'
              value={modalRoomName}
              onChange={(e) => setModalRoomName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSaveRoom();
                }
              }}
            />

            {data.zones.length > 0 && (
              <TextField
                select
                fullWidth
                label='Zone'
                size='small'
                value={modalRoomZoneId}
                onChange={(e) => setModalRoomZoneId(e.target.value)}
              >
                <MenuItem value=''>No zone</MenuItem>
                {data.zones.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              label='Capacity'
              placeholder='e.g 4 (empty = no limit)'
              size='small'
              type='number'
              value={modalRoomCapacity}
              onChange={(e) => setModalRoomCapacity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSaveRoom();
                }
              }}
              inputProps={{ min: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRoomModal}>Cancel</Button>
          <Button onClick={handleSaveRoom} disabled={!modalRoomName.trim()} variant='contained'>
            {editingRoom ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
